import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Clock, CreditCard, Check, Crown, ExternalLink, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { usePlatform } from '../hooks/usePlatform';
import { Button } from './ui/button';
import { toast } from 'sonner';

const Paywall = ({ story, onClose }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { subscription, startTrial, subscribe, isPremium } = useSubscription();
  const { hideWebPurchaseUI } = usePlatform();
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState('monthly'); // 'monthly' | 'yearly'

  const handleStartTrial = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setLoading(true);
    const result = await startTrial();
    setLoading(false);
    if (result.success) {
      toast.success(`🎉 Proefperiode gestart! ${result.data.days} dagen gratis.`);
      onClose?.();
    } else { toast.error(result.error); }
  };

  const handleSubscribe = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setLoading(true);
    const result = await subscribe(plan);
    setLoading(false);
    if (result.success && !result.redirect) {
      toast.success('🎉 Abonnement geactiveerd!');
      onClose?.();
    } else if (!result.success) {
      toast.error(result.error);
    }
  };

  if (isPremium) return null;

  const isYearly = plan === 'yearly';
  const priceText = isYearly ? '€29,90' : '€2,99';
  const periodText = isYearly ? '/jaar' : '/maand';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-night-blue/85 backdrop-blur-md"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))', paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="paywall-overlay"
    >
      <motion.div
        initial={{ scale: 0.92, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        className="w-full max-w-md bg-card rounded-2xl shadow-2xl overflow-hidden border border-gold/30 max-h-[92vh] flex flex-col"
        data-testid="paywall-card"
      >
        {/* Header */}
        <div className="relative h-20 sm:h-24 bg-gradient-to-br from-old-rose/30 to-gold/20 overflow-hidden flex-shrink-0">
          {story?.cover_image_url && (
            <img
              src={story.cover_image_url}
              alt={story.title || ''}
              className="absolute inset-0 w-full h-full object-cover opacity-30 blur-sm"
            />
          )}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-white/80 hover:bg-white flex items-center justify-center"
            aria-label="Sluiten"
            data-testid="paywall-close"
          >
            <X className="w-4 h-4 text-night-blue" />
          </button>
          <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
            <div className="w-9 h-9 rounded-full bg-night-blue/80 flex items-center justify-center mb-1">
              <Lock className="w-4 h-4 text-gold" />
            </div>
            <h3 className="font-display text-base sm:text-lg text-night-blue text-center leading-tight line-clamp-1">
              {story?.title || 'Premium Verhaal'}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-3 space-y-3 overflow-y-auto">
          {/* Title */}
          <div className="text-center">
            <h2 className="font-display text-xl flex items-center justify-center gap-1.5">
              <Crown className="w-5 h-5 text-gold" />
              Word Premium
            </h2>
            <p className="text-xs text-muted-foreground">Onbeperkt toegang tot alle verhalen</p>
          </div>

          {/* Compact benefits — 2x2 grid */}
          <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-[13px] text-foreground">
            {[
              'Alle verhalen',
              'Nieuwe elke week',
              'Bedtijdmodus',
              'Geen advertenties',
            ].map((b) => (
              <div key={b} className="flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-green-600 shrink-0" />
                <span>{b}</span>
              </div>
            ))}
          </div>

          {/* Plan toggle + price */}
          {!hideWebPurchaseUI && (
            <div className="rounded-xl bg-gradient-to-r from-gold/10 to-old-rose/10 p-3" data-testid="paywall-pricing">
              <div className="grid grid-cols-2 gap-1 p-0.5 bg-muted/60 rounded-lg mb-2 text-[11px] font-bold">
                <button
                  onClick={() => setPlan('monthly')}
                  className={`py-1.5 rounded-md transition-colors ${plan === 'monthly' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}
                  data-testid="paywall-plan-monthly"
                >
                  Maandelijks
                </button>
                <button
                  onClick={() => setPlan('yearly')}
                  className={`relative py-1.5 rounded-md transition-colors ${plan === 'yearly' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}
                  data-testid="paywall-plan-yearly"
                >
                  Jaarlijks
                  <span className="absolute -top-1.5 -right-1 bg-green-500 text-white text-[8px] font-bold px-1 py-px rounded">
                    -2 mnd
                  </span>
                </button>
              </div>
              <div className="text-center">
                <span className="text-2xl font-bold text-foreground">{priceText}</span>
                <span className="text-xs text-muted-foreground ml-1">{periodText}</span>
                {isYearly && (
                  <div className="text-[11px] text-green-600 font-bold">je bespaart €5,98 per jaar</div>
                )}
              </div>
            </div>
          )}

          {/* Buttons */}
          {!hideWebPurchaseUI ? (
            <div className="space-y-2">
              {subscription?.trial_available && (
                <Button
                  data-testid="start-trial-btn"
                  onClick={handleStartTrial}
                  disabled={loading}
                  className="w-full rounded-full bg-gold hover:bg-gold/90 text-night-blue font-bold py-2.5 text-sm shadow-md"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-night-blue/30 border-t-night-blue rounded-full animate-spin" />
                  ) : (
                    <><Clock className="w-4 h-4 mr-1.5" /> Start 7 dagen gratis</>
                  )}
                </Button>
              )}
              <Button
                data-testid="subscribe-btn"
                onClick={handleSubscribe}
                disabled={loading}
                variant={subscription?.trial_available ? 'outline' : 'default'}
                className={`w-full rounded-full font-bold py-2.5 text-sm ${
                  !subscription?.trial_available ? 'bg-gold hover:bg-gold/90 text-night-blue shadow-md' : ''
                }`}
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                ) : (
                  <><CreditCard className="w-4 h-4 mr-1.5" /> Direct abonneren</>
                )}
              </Button>
            </div>
          ) : (
            <div className="p-3 rounded-xl bg-muted/60 border border-border text-center" data-testid="paywall-ios-notice">
              <p className="text-foreground font-medium text-sm mb-1">Premium beheren op web</p>
              <p className="text-xs text-muted-foreground mb-2">
                Start je abonnement op omasverhaaltjes.nl en log hier in met hetzelfde account.
              </p>
              <a
                href="https://omasverhaaltjes.nl/subscription"
                className="inline-flex items-center gap-1 text-xs text-gold font-semibold hover:underline"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="paywall-ios-link"
              >
                Ga naar omasverhaaltjes.nl <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}

          {/* Footer link */}
          <button
            onClick={onClose}
            className="block w-full text-center text-xs text-muted-foreground hover:text-foreground py-1"
            data-testid="paywall-later"
          >
            Misschien later
          </button>

          {!isAuthenticated && (
            <p className="text-center text-[11px] text-muted-foreground">
              Al een account?{' '}
              <button onClick={() => navigate('/login')} className="text-gold hover:underline font-medium">
                Log in
              </button>
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Paywall;
